import React from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  createStyles,
  IconButton,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Theme,
  Typography
} from '@material-ui/core';
import Grid from '@material-ui/core/Grid/Grid';
import { useTranslation } from 'react-i18next';
import utils from '../../services/utils.service';
import { useRevolusendApi } from '../../context';
import notFoundImg from '../../assets/img/not-found.svg';
import Autocomplete from '@material-ui/lab/Autocomplete/Autocomplete';
import DetailsIcon from '@material-ui/icons/Info';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import { Tenant } from '../../services/types/revolusend/tenant.type';
import { DecoratedRevolupayUser } from '../../services/types/revolusend/decoratedRevolupayUser.type';
import { KycStatus } from '../../services/types/revolusend/revolupayUser.type';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    img: {
      width: 400,
      maxWidth: '100%',
      display: 'block',
      margin: '0 auto',
    },
    noBottomBorder: {
      '& > *': {
        borderBottom: 'unset',
      },
    },
  })
);



const Row = (props: { user: DecoratedRevolupayUser }) => {
  const { user } = props;
  const history = useHistory();
  const { t } = useTranslation(['revolusendUsers']);

  const getStatusColor = () => {
    if (user.user.status === KycStatus.KYC_OK) {
      return 'primary';
    } else if (user.user.status === KycStatus.KYC_KO) {
      return 'secondary';
    } else {
      return 'default';
    }
  }

  return (
    <React.Fragment>
      <TableRow>
        <TableCell>
          <a href={'/revolusend/users/' + user.user.id} target='_blank'>
            <IconButton size='small'>
              <DetailsIcon />
            </IconButton>
          </a>
        </TableCell>
        <TableCell>
          {user.tenant.name}
        </TableCell>
        <TableCell>
          {user.user.id}
        </TableCell>
        <TableCell>
          <Chip color={getStatusColor()} label={t(user.user.status)} />
        </TableCell>
        <TableCell>
          {moment(user.user.created).format('DD-MM-YYYY HH:mm:ss')}
        </TableCell>
        <TableCell>
          {user.user.updated ? moment(user.user.updated).format('DD-MM-YYYY HH:mm:ss') : '-'}
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

export const Users = () => {
  const { t } = useTranslation(['revolusendUsers']);
  const api = useRevolusendApi();
  const [count, setCount] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [users, setUsers] = React.useState<DecoratedRevolupayUser[]>([]);

  const [tenants, setTenants] = React.useState<Tenant[]>([]);
  const [selectedTenant, selectTenant] = React.useState<Tenant | null>(null);

  const states = [
    KycStatus.KYC_PENDING,
    KycStatus.KYC_PROCESSING,
    KycStatus.KYC_OK,
    KycStatus.KYC_KO
  ];

  const [selectedStatus, selectStatus] = React.useState<KycStatus | null>(null);

  const [page, setPage] = React.useState(0);


  const limit = 25;

  const classes = useStyles();

  const loadData = () => {
    utils.runAsync(async () => {
      setLoading(true);
      const { users, count } = await api.listRevolupayUsers({
        tenantId: selectedTenant?.id,
        status: selectedStatus ? selectedStatus : undefined,
      }, limit, page * limit, 'desc');
      setUsers(users);
      setCount(count);
    }, () => {
      setLoading(false);
    })
  }

  React.useEffect(() => {
    utils.runAsync(async () => {
      setTenants(await api.listTenants());
    });
  }, []);

  React.useEffect(loadData, [page]);

  React.useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setPage(0);
      loadData();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [
    selectedStatus,
    selectedTenant,
  ]);

  return (
    <Grid container spacing={2}>
      <Grid item container xs={12}>
        <Grid item container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Autocomplete
              onChange={(event, value) => {
                selectTenant(value);
              }}
              value={selectedTenant}
              options={tenants}
              autoHighlight
              disabled={loading}
              getOptionLabel={(option) => option.name}
              getOptionSelected={(option, value) => option.id === value.id}
              renderOption={(option) => option.name}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('SEARCH_TENANT')}
                  variant='standard'
                  fullWidth={true}
                  InputProps={{
                    ...params.InputProps,
                  }}
                  inputProps={{
                    ...params.inputProps,
                    autoComplete: 'new-password',
                  }}
                />
              )} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Autocomplete
              onChange={(event, value) => {
                selectStatus(value);
              }}
              value={selectedStatus}
              options={states}
              autoHighlight
              disabled={loading}
              getOptionLabel={(option) => t(option)}
              getOptionSelected={(option, value) => option === value}
              renderOption={(option) => t(option)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('SEARCH_STATUS')}
                  variant='standard'
                  fullWidth={true}
                  InputProps={{
                    ...params.InputProps,
                  }}
                  inputProps={{
                    ...params.inputProps,
                    autoComplete: 'new-password',
                  }}
                />
              )} />
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <Paper>
          <Grid container>

            <Grid item xs={12}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell></TableCell>
                      <TableCell>{t('TENANT')}</TableCell>
                      <TableCell>{t('ID')}</TableCell>
                      <TableCell>{t('STATUS')}</TableCell>
                      <TableCell>{t('CREATED')}</TableCell>
                      <TableCell>{t('UPDATED')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map(u =>
                      <Row
                        user={u}
                        key={u.user.id}
                      />)}
                    {loading && users.length === 0 &&
                      <TableCell colSpan={6}>
                        <Box padding={2}>
                          <Grid container justify='center' item xs={12}>
                            <CircularProgress />
                          </Grid>
                        </Box>
                      </TableCell>
                    }
                    {!loading && users.length === 0 &&
                      <TableCell colSpan={6}>
                        <Box padding={2}>
                          <Typography align='center' variant='h5'>{t('NO_DATA')}</Typography>
                          <img className={classes.img} src={notFoundImg} />
                        </Box>
                      </TableCell>
                    }
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component='div'
                rowsPerPageOptions={[limit]}
                count={count}
                rowsPerPage={limit}
                page={page}
                onChangePage={(event, page) => { setPage(page) }}
              />
            </Grid>
          </Grid>
        </Paper>
      </Grid>
    </Grid >
  )
}
